import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { usePersona } from '@/app/providers';
import { useScenario } from '@/versions/v1/shared/ScenarioProvider';
import type { Persona } from '@/shared/types';
import type { PlantRoleId } from '@/versions/v1/schema/plant-role';
import type { SeatId } from '@/versions/v1/schema/seat';
import { PRIMARY_SEAT_BY_ROLE } from '@/versions/v1/shared/bunker-hill-org-seed';
import { CASES, FAILURE_MODES } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { personaCan } from './RoleContext';
import {
  approveFmeaProposal,
  failureModeIdForCauseId,
  proposeFmeaChange,
  INITIAL_FMEA_LEARNING_STATE,
  type FmeaLearningSessionState,
} from './FmeaLearningEngine';
import {
  startedInvestigationRun,
  type InvestigationRun,
} from '@/versions/v1/modules/advisory/InvestigationRun.transform';
import type { AgentTrace } from '@/shared/contracts/analytics/v1';

function plantRoleForPersona(persona: Persona): PlantRoleId {
  switch (persona) {
    case 'GM':
      return 'gm';
    case 'Metallurgist':
      return 'metallurgist';
    case 'Senior metallurgist':
      return 'senior-metallurgist';
    case 'Maintenance':
      return 'maintenance';
    case 'Superintendent':
      return 'superintendent';
    case 'Control room':
      return 'cro';
    case 'Operator':
      return 'operator';
    case 'Ops supervisor':
      return 'ops-supervisor';
    case 'Admin':
    case 'Editor':
    case 'Reviewer':
    case 'Viewer':
      return 'metallurgist';
  }
}

// route-reads paul-feedback.A8 hard rule 1 — a decision names a seat, not a
// role. `PRIMARY_SEAT_BY_ROLE` has no entry for `ops-supervisor` (no seat
// seed represents that role yet); fall back to the metallurgist seat rather
// than let a decision-recording call silently receive `undefined`.
function seatForPersona(persona: Persona): SeatId {
  const role = plantRoleForPersona(persona);
  return PRIMARY_SEAT_BY_ROLE[role] ?? 'seat-met';
}

import {
  ackAlarm,
  requestSamples,
  approveAction,
  rejectAction,
  implementAction,
  approveChange,
  answerEvidence,
  INITIAL_ADVISORY_STATE,
  rejectChange,
  reviewChange,
  pinCaseForReport,
  unpinCaseFromReport,
  type AdvisorySessionState,
} from './AdvisoryEngine';

type AdvisoryContextValue = AdvisorySessionState & {
  fmeaLearning: FmeaLearningSessionState;
  /**
   * K15 / D24: investigation runs started on the glass this session. The
   * recorded run is derived per surface from the committed trace; this holds
   * only what pressing `Run investigation` starts, so the landing's Diagnosis
   * stage and the Workflows catalogue list the same runs the case page played.
   */
  investigationRuns: readonly InvestigationRun[];
  /**
   * K15 round 2: the run the case column is streaming right now. Every surface
   * that lists runs reads it, so a playing run reads `Running` rather than
   * `At gate` on the landing and in the catalogue while it is still playing.
   */
  playingInvestigationRunId: string | null;
  /** The case column reports its live playback here (`null` once it reaches the gate). */
  setInvestigationRunPlaying: (runId: string | null) => void;
  /** Start a run of the Excursion investigation workflow at the replay cursor. */
  startInvestigationRun: (input: {
    /** The runs the surface already holds, so the new id follows the recorded run. */
    existing?: readonly InvestigationRun[];
    caseId: string;
    caseCode: string;
    fixture: string;
    trace: AgentTrace;
  }) => InvestigationRun;
  /** Action gate (D19): the approved action raises the case's FMEA change as a draft proposal. */
  recordActionApproval: (caseId: string, causeId: string, earlyWarning?: string, reason?: string) => void;
  /** FMEA gate (D19): the Senior Metallurgist seat approves the proposal; only then is the row live. */
  approveFmeaProposal: (caseId: string) => void;
  approveAction: (actionId: string) => void;
  rejectAction: (actionId: string, reason: string) => void;
  implementAction: (actionId: string) => void;
  answerEvidence: (evidenceId: string, answer: string) => void;
  ackAlarm: (alarmId: string) => void;
  requestSamples: (divergenceId: string, samplePointId?: string) => void;
  reviewChange: (reviewId: string, notes: string) => void;
  approveChange: (reviewId: string) => void;
  rejectChange: (reviewId: string, reason: string) => void;
  pinCaseForReport: (caseId: string) => void;
  unpinCaseFromReport: (caseId: string) => void;
};

const AdvisoryContext = createContext<AdvisoryContextValue | null>(null);

export function AdvisoryProvider({ children }: { children: ReactNode }) {
  const { state: scenarioState } = useScenario();
  const [state, setState] = useState<AdvisorySessionState>(INITIAL_ADVISORY_STATE);
  const [fmeaLearning, setFmeaLearning] = useState<FmeaLearningSessionState>(INITIAL_FMEA_LEARNING_STATE);
  const [investigationRuns, setInvestigationRuns] = useState<InvestigationRun[]>([]);
  const [playingInvestigationRunId, setInvestigationRunPlaying] = useState<string | null>(null);
  const { persona } = usePersona();
  const authorRole = plantRoleForPersona(persona);
  const actingSeat = seatForPersona(persona);

  const onApproveAction = useCallback(
    (actionId: string) => {
      setState((current) =>
        approveAction(current, actionId, scenarioState.cursor, actingSeat),
      );
    },
    [scenarioState.cursor, actingSeat],
  );

  const onRejectAction = useCallback(
    (actionId: string, reason: string) => {
      setState((current) =>
        rejectAction(current, actionId, scenarioState.cursor, actingSeat, reason),
      );
    },
    [scenarioState.cursor, actingSeat],
  );

  const onImplementAction = useCallback(
    (actionId: string) => {
      setState((current) => implementAction(current, actionId, scenarioState.cursor));
    },
    [scenarioState.cursor],
  );

  const onAnswerEvidence = useCallback((evidenceId: string, answer: string) => {
    setState((current) => answerEvidence(current, evidenceId, answer));
  }, []);

  const onAckAlarm = useCallback((alarmId: string) => {
    setState((current) => ackAlarm(current, alarmId));
  }, []);

  const onRequestSamples = useCallback(
    (divergenceId: string, samplePointId?: string) => {
      setState((current) =>
        requestSamples(current, divergenceId, scenarioState.cursor, scenarioState.cadence, samplePointId, authorRole),
      );
      },
    [scenarioState.cursor, scenarioState.cadence, authorRole],
  );

  const onReviewChange = useCallback(
    (reviewId: string, notes: string) => {
      setState((current) =>
        reviewChange(current, reviewId, notes, scenarioState.cursor, {
          cursor: scenarioState.cursor,
          authorRole,
        }),
      );
    },
    [scenarioState.cursor, authorRole],
  );

  const onApproveChange = useCallback(
    (reviewId: string) => {
      setState((current) =>
        approveChange(current, reviewId, {
          cursor: scenarioState.cursor,
          authorRole,
        }),
      );
    },
    [scenarioState.cursor, authorRole],
  );

  const onRejectChange = useCallback((reviewId: string, reason: string) => {
    setState((current) => rejectChange(current, reviewId, reason));
  }, []);

  const onPinCaseForReport = useCallback((caseId: string) => {
    setState((current) => pinCaseForReport(current, caseId));
  }, []);

  const onUnpinCaseFromReport = useCallback((caseId: string) => {
    setState((current) => unpinCaseFromReport(current, caseId));
  }, []);

  const onStartInvestigationRun = useCallback(
    (input: {
      existing?: readonly InvestigationRun[];
      caseId: string;
      caseCode: string;
      fixture: string;
      trace: AgentTrace;
    }) => {
      const run = startedInvestigationRun({
        runs: input.existing ?? investigationRuns,
        caseId: input.caseId,
        caseCode: input.caseCode,
        fixture: input.fixture,
        cursor: scenarioState.cursor,
        trace: input.trace,
      });
      setInvestigationRuns((current) => [...current, run]);
      return run;
    },
    [investigationRuns, scenarioState.cursor],
  );

  const onRecordActionApproval = useCallback(
    (caseId: string, causeId: string, earlyWarning?: string, reason?: string) => {
      const failureModeId = failureModeIdForCauseId(causeId);
      const failureMode = FAILURE_MODES.find((row) => row.id === failureModeId);
      const caseItem = CASES.find((row) => row.id === caseId);
      if (!failureMode || !caseItem) return;
      setFmeaLearning((current) => proposeFmeaChange(current, {
        caseId,
        caseCode: caseItem.code ?? caseId,
        causeId,
        failureMode,
        excursionDate: caseItem.raisedAt,
        proposedAt: scenarioState.cursor,
        proposedBy: persona === 'Metallurgist' ? 'Control metallurgist' : persona,
        earlyWarning,
        // K13c: the case's own lesson rides with the proposal, never typed here.
        learnedCause: caseItem.learnedCause,
        learnedEarlyWarning: caseItem.learnedEarlyWarning,
        reason,
      }));
    },
    [scenarioState.cursor, persona],
  );

  // A decision names a seat, not a person: the record reads "Senior
  // metallurgist" whoever holds the seat at this site (the GM at Bunker Hill).
  const onApproveFmeaProposal = useCallback(
    (caseId: string) => {
      if (!personaCan(persona, 'fmea:approve-proposal')) return;
      setFmeaLearning((current) => approveFmeaProposal(current, {
        caseId,
        approvedAt: scenarioState.cursor,
        approvedBy: 'Senior metallurgist',
      }));
    },
    [scenarioState.cursor, persona],
  );

  const value = useMemo<AdvisoryContextValue>(
    () => ({
      ...state,
      fmeaLearning,
      investigationRuns,
      playingInvestigationRunId,
      setInvestigationRunPlaying,
      startInvestigationRun: onStartInvestigationRun,
      recordActionApproval: onRecordActionApproval,
      approveFmeaProposal: onApproveFmeaProposal,
      approveAction: onApproveAction,
      rejectAction: onRejectAction,
      implementAction: onImplementAction,
      answerEvidence: onAnswerEvidence,
      ackAlarm: onAckAlarm,
      requestSamples: onRequestSamples,
      reviewChange: onReviewChange,
      approveChange: onApproveChange,
      rejectChange: onRejectChange,
      pinCaseForReport: onPinCaseForReport,
      unpinCaseFromReport: onUnpinCaseFromReport,
    }),
    [
      state,
      fmeaLearning,
      investigationRuns,
      playingInvestigationRunId,
      onStartInvestigationRun,
      onRecordActionApproval,
      onApproveFmeaProposal,
      onApproveAction,
      onRejectAction,
      onImplementAction,
      onAnswerEvidence,
      onAckAlarm,
      onRequestSamples,
      onReviewChange,
      onApproveChange,
      onRejectChange,
      onPinCaseForReport,
      onUnpinCaseFromReport,
    ],
  );

  return <AdvisoryContext.Provider value={value}>{children}</AdvisoryContext.Provider>;
}

export function useAdvisory() {
  const ctx = useContext(AdvisoryContext);
  if (!ctx) throw new Error('useAdvisory must be used inside <AdvisoryProvider>');
  return ctx;
}
